import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth.store'

// Configurable via env — default 30 minutes
const TIMEOUT_MS = (Number(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES) || 30) * 60 * 1000
// Warning shown 5 minutes before auto-logout
const WARN_BEFORE_MS = 5 * 60 * 1000

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'] as const

/**
 * SessionManager — inactivity timeout with warning modal.
 * Mount inside AppLayout so it only runs for authenticated pages.
 *
 * Flow:
 *  - Any user activity resets the idle timer
 *  - After (TIMEOUT_MS - WARN_BEFORE_MS) of inactivity → show warning
 *  - If user clicks "Rimani connesso" → timer resets
 *  - After TIMEOUT_MS total → auto-logout
 */
export default function SessionManager() {
  const { t } = useTranslation()
  const navigate       = useNavigate()
  const logout         = useAuthStore(s => s.logout)
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)

  const [showWarning, setShowWarning] = useState(false)
  const [countdown,   setCountdown]   = useState(WARN_BEFORE_MS / 1000)

  const warnTimer   = useRef<ReturnType<typeof setTimeout> | null>(null)
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownInterval = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearAllTimers = useCallback(() => {
    if (warnTimer.current)        clearTimeout(warnTimer.current)
    if (logoutTimer.current)      clearTimeout(logoutTimer.current)
    if (countdownInterval.current) clearInterval(countdownInterval.current)
    warnTimer.current = logoutTimer.current = countdownInterval.current = null
  }, [])

  const doLogout = useCallback(() => {
    clearAllTimers()
    setShowWarning(false)
    logout()
    navigate('/login', { replace: true })
  }, [clearAllTimers, logout, navigate])

  const startTimers = useCallback(() => {
    clearAllTimers()
    setShowWarning(false)

    warnTimer.current = setTimeout(() => {
      setShowWarning(true)
      setCountdown(WARN_BEFORE_MS / 1000)
      countdownInterval.current = setInterval(() => {
        setCountdown(c => {
          if (c <= 1) {
            if (countdownInterval.current) clearInterval(countdownInterval.current)
            return 0
          }
          return c - 1
        })
      }, 1000)
    }, TIMEOUT_MS - WARN_BEFORE_MS)

    logoutTimer.current = setTimeout(doLogout, TIMEOUT_MS)
  }, [clearAllTimers, doLogout])

  const resetActivity = useCallback(() => {
    if (!isAuthenticated) return
    startTimers()
  }, [isAuthenticated, startTimers])

  // Register activity listeners
  useEffect(() => {
    if (!isAuthenticated) return
    startTimers()

    const handle = () => resetActivity()
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, handle, { passive: true }))
    return () => {
      clearAllTimers()
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, handle))
    }
  }, [isAuthenticated, startTimers, resetActivity, clearAllTimers])

  if (!showWarning) return null

  const mins = Math.floor(countdown / 60)
  const secs = countdown % 60
  const timeStr = mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : `${secs}s`

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 text-center space-y-5">
        <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>

        <div>
          <h2 className="text-xl font-bold text-gray-900">{t('auto.componentsSessionManager.sessioneInScadenza')}</h2>
          <p className="text-sm text-gray-500 mt-1">
            Sei inattivo da un po'. Verrai disconnesso automaticamente tra:
          </p>
          <p className="text-4xl font-mono font-bold text-amber-600 mt-3">{timeStr}</p>
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={resetActivity}
            className="w-full py-3 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl transition-colors"
          >
            Rimani connesso
          </button>
          <button
            onClick={doLogout}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Disconnetti ora
          </button>
        </div>
      </div>
    </div>
  )
}
